import { CANDLE_COLOR, CHART_COLORS } from "@/app/theme/theme";
import { selectLogBase } from "@/lib/logBase";

export function lineChartOption(
  x: { title: string; data: any[] },
  dataset: { title: string; data: number[]; yAxisIndex?: number }[],
  colors?: string[],
  yAxisOptions?: { [yAxisIndex: number]: { useLogScale?: boolean } }
) {
  const localColors = colors ? colors : CHART_COLORS;
  const legends = dataset.map((data) => data.title);

  // 为没有指定 yAxisIndex 的数据系列分配默认值
  const processedDataset = dataset.map((item, index) => ({
    ...item,
    yAxisIndex: item.yAxisIndex !== undefined ? item.yAxisIndex : index,
  }));

  // 根据处理后的 dataset 中的 yAxisIndex 确定需要多少个 Y 轴
  const maxYAxisIndex = Math.max(0, ...processedDataset.map((item) => item.yAxisIndex));
  const yAxisCount = maxYAxisIndex + 1;

  const yAxis = [];
  for (let i = 0; i < yAxisCount; i++) {
    const yOpt = yAxisOptions?.[i] || {};
    const isLog = !!yOpt.useLogScale;

    // 自动计算 logBase
    let logBase: number | undefined = undefined;
    if (isLog) {
      const axisData = processedDataset
        .filter((item) => item.yAxisIndex === i)
        .flatMap((item) => item.data)
        .filter((v: number) => v > 0);
      if (axisData.length > 0) {
        const minValue = Math.min(...axisData);
        const maxValue = Math.max(...axisData);
        logBase = selectLogBase(minValue, maxValue);
      } else {
        logBase = 10;
      }
    }

    yAxis.push({
      type: isLog ? "log" : "value",
      logBase: isLog ? logBase : undefined,
      axisLine: {
        show: false,
      },
      axisLabel: {
        color: localColors[i],
        show: i <= 2,
        formatter: yLabelFormatter(i),
      },
      splitLine: { show: i == 0 },
      position: i === 0 ? "left" : "right",
      offset: i > 1 ? 30 * (i - 1) : 0,
      alignTicks: isLog ? undefined : i !== 0,
      scale: true,
    });
  }

  const option = {
    ...{ color: localColors },
    tooltip: {
      trigger: "axis" as "axis",
    },
    legend: {
      data: legends,
      left: "left",
      bottom: 0,
    },
    grid: {
      left: "60px",
      right: "60px",
    },
    xAxis: {
      type: "category" as const,
      data: x.data,
      axisLine: {
        onZero: false,
      },
    },
    yAxis: yAxis,
    series: processedDataset.map((item, index) => {
      return {
        name: item.title,
        type: "line",
        data: item.data,
        yAxisIndex: item.yAxisIndex,
        showSymbol: false,
      };
    }),
  };
  // console.log(option);
  return option;
}

function yLabelFormatter(i: number) {
  return (value: number) => {
    if (i == 0) {
      return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
    }
    const abs = Math.abs(value);
    const sign = value < 0 ? "-" : "";

    // 保证至少2位有效数字，末尾0可省略
    const format = (num: number, unit: string) => {
      // 计算整数位数
      const intLen = Math.floor(num).toString().replace("-", "").length;
      // 保证至少2位有效数字
      const minDigits = 3;
      const decimals = Math.max(0, minDigits - intLen);
      // toFixed后去掉多余的0
      return `${sign}${parseFloat(num.toFixed(decimals))}${unit}`;
    };

    if (abs >= 1e12) return format(abs / 1e12, "T");
    if (abs >= 1e9) return format(abs / 1e9, "B");
    if (abs >= 1e6) return format(abs / 1e6, "M");
    if (abs >= 1e3) return format(abs / 1e3, "k");
    return format(abs, "");
  };
}

export function lineStackBarChartOption(
  x: { title: string; data: any[] },
  dataset: { title: string; data: number[]; yAxisIndex?: number }[],
  stackBar: { title: string; data: number[]; yAxisIndex?: number }[]
) {
  const legends = dataset.map((data) => data.title).concat(stackBar.map((data) => data.title));

  // 为没有指定 yAxisIndex 的数据系列分配默认值
  const processedDataset = dataset.map((item, index) => ({
    ...item,
    yAxisIndex: item.yAxisIndex !== undefined ? item.yAxisIndex : index,
  }));
  const processedStackBar = stackBar.map((item, index) => ({
    ...item,
    yAxisIndex: item.yAxisIndex !== undefined ? item.yAxisIndex : dataset.length,
  }));

  const yAxis = [];
  for (let i = 0; i < processedDataset.length; i++) {
    // 只有第0个y轴支持log
    let isLog = false;
    let logBase: number | undefined = undefined;
    if (i === 0) {
      // 自动判断是否需要log
      const axisData = dataset[0].data.filter((v: number) => v > 0);
      if (axisData.length > 0) {
        const minValue = Math.min(...axisData);
        const maxValue = Math.max(...axisData);
        logBase = selectLogBase(minValue, maxValue);
        isLog = true;
      }
    }
    yAxis.push({
      type: isLog ? "log" : "value",
      logBase: isLog ? logBase : undefined,
      axisLine: {
        show: false,
      },
      axisLabel: {
        color: CHART_COLORS[i],
        show: i <= 2,
        formatter: yLabelFormatter(i),
      },
      position: i === 0 ? "left" : "right",
      offset: i > 1 ? 30 * (i - 1) : 0,
      alignTicks: i !== 0,
      scale: true,
    });
  }
  yAxis.push({
    type: "value" as const,
    axisLine: {
      show: false,
    },
    axisLabel: {
      show: false,
    },
    splitLine: {
      show: false,
    },
  });
  let series = processedDataset.map((item, index) => {
    return {
      name: item.title,
      type: "line",
      data: item.data,
      yAxisIndex: item.yAxisIndex,
      showSymbol: false,
    };
  });
  series = series.concat(
    processedStackBar.map((item, index) => {
      return {
        name: item.title,
        type: "bar",
        stack: "stackbar",
        data: item.data,
        yAxisIndex: item.yAxisIndex,
        showSymbol: false,
        itemStyle: {
          color: index === 0 ? CANDLE_COLOR.BULLISH : CANDLE_COLOR.BEARISH,
        },
      };
    })
  );
  const option = {
    tooltip: {
      trigger: "axis" as "axis",
    },
    legend: {
      data: legends,
      left: "left",
      bottom: 0,
    },
    grid: {
      left: "60px",
      right: "70px",
    },
    xAxis: {
      type: "category" as const,
      data: x.data,
      // axisLabel: {
      //   formatter: (value: any) => dayjs(value).format("YYYY-MM-DD"),
      // },
    },
    yAxis: yAxis,
    series: series,
  };
  return option;
}

export function setLineAreaGradient(opt: any) {
  (opt.series[0] as any).areaStyle = {
    color: {
      type: "linear",
      x: 0, // 渐变起始点的 x 坐标 (0为左边界)
      y: 0, // 渐变起始点的 y 坐标 (0为上边界)
      x2: 0, // 渐变终点的 x 坐标
      y2: 1, // 渐变终点的 y 坐标 (1为下边界)。此设置创建从上到下的垂直渐变。
      colorStops: [
        {
          offset: 0,
          color: "#CC9E5A", // 起点颜色，较深
        },
        {
          offset: 1,
          color: "#CC9E5A00", // 终点颜色，完全透明
        },
      ],
    },
  };
};
